import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect } from 'react';
import { auditApi } from '@/api/auditApi';
import { PageHeader } from '@/components/shared/PageHeader';
import { DataTable } from '@/components/shared/DataTable';
import { ErrorAlert } from '@/components/shared/ErrorAlert';
export const AuditLogPage = () => {
    const [logs, setLogs] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [totalLogs, setTotalLogs] = useState(0);
    const [filters, setFilters] = useState({
        page: 1,
        perPage: 20,
    });
    useEffect(() => {
        loadLogs();
    }, [filters]);
    const loadLogs = async () => {
        setIsLoading(true);
        setError('');
        try {
            const response = await auditApi.getLogs(filters);
            setLogs(response.logs);
            setTotalLogs(response.total);
        }
        catch (err) {
            setError('Failed to load audit logs. Please try again.');
            console.error('Load logs error:', err);
        }
        finally {
            setIsLoading(false);
        }
    };
    const handleExport = async () => {
        try {
            const blob = await auditApi.exportToCSV(filters);
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `audit-log-${new Date().toISOString()}.csv`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        }
        catch (err) {
            setError('Failed to export audit logs.');
            console.error('Export error:', err);
        }
    };
    const handleActionFilter = (action) => {
        setFilters((prev) => ({
            ...prev,
            action: action || undefined,
            page: 1,
        }));
    };
    const columns = [
        {
            key: 'timestamp',
            label: 'Timestamp',
            render: (log) => (_jsx("span", { className: "text-sm text-gray-900", children: new Date(log.timestamp).toLocaleString() })),
        },
        {
            key: 'adminEmail',
            label: 'Admin',
            render: (log) => _jsx("span", { className: "text-sm font-medium text-gray-900", children: log.adminEmail }),
        },
        {
            key: 'action',
            label: 'Action',
            render: (log) => (_jsx("span", { className: "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800", children: log.action.replace(/_/g, ' ') })),
        },
        {
            key: 'targetUserEmail',
            label: 'Target User',
            render: (log) => (_jsx("span", { className: "text-sm text-gray-600", children: log.targetUserEmail || '-' })),
        },
        {
            key: 'ipAddress',
            label: 'IP Address',
            render: (log) => _jsx("span", { className: "text-sm text-gray-500", children: log.ipAddress || '-' }),
        },
        {
            key: 'details',
            label: 'Details',
            render: (log) => (_jsx("button", { onClick: () => {
                    alert(JSON.stringify(log.details, null, 2));
                }, className: "text-xs text-primary-600 hover:text-primary-900", children: "View JSON" })),
        },
    ];
    return (_jsxs("div", { children: [_jsx(PageHeader, { title: "Audit Log", subtitle: `${totalLogs} total entries`, action: _jsx("button", { onClick: handleExport, className: "btn-primary", children: "Export CSV" }) }), error && (_jsx("div", { className: "mb-6", children: _jsx(ErrorAlert, { message: error, onDismiss: () => setError('') }) })), _jsxs("div", { className: "card p-4 mb-6", children: [_jsxs("div", { className: "grid grid-cols-1 md:grid-cols-4 gap-4", children: [_jsx("input", { type: "text", placeholder: "User ID...", onChange: (e) => setFilters((prev) => ({
                                    ...prev,
                                    userId: e.target.value || undefined,
                                    page: 1,
                                })), className: "input" }), _jsx("input", { type: "text", placeholder: "Admin ID...", onChange: (e) => setFilters((prev) => ({
                                    ...prev,
                                    adminId: e.target.value || undefined,
                                    page: 1,
                                })), className: "input" }), _jsxs("select", { onChange: (e) => handleActionFilter(e.target.value), className: "input", children: [_jsx("option", { value: "", children: "All Actions" }), _jsx("option", { value: "USER_CREATED", children: "User Created" }), _jsx("option", { value: "USER_UPDATED", children: "User Updated" }), _jsx("option", { value: "USER_SUSPENDED", children: "User Suspended" }), _jsx("option", { value: "USER_BANNED", children: "User Banned" }), _jsx("option", { value: "USER_RESTORED", children: "User Restored" }), _jsx("option", { value: "USER_DELETED", children: "User Deleted" })] }), _jsx("button", { onClick: loadLogs, className: "btn-secondary", children: "Refresh" })] }), _jsxs("div", { className: "grid grid-cols-1 md:grid-cols-2 gap-4 mt-4", children: [_jsxs("div", { children: [_jsx("label", { className: "block text-sm font-medium text-gray-700 mb-1", children: "Start Date" }), _jsx("input", { type: "date", onChange: (e) => setFilters((prev) => ({
                                            ...prev,
                                            startDate: e.target.value || undefined,
                                            page: 1,
                                        })), className: "input" })] }), _jsxs("div", { children: [_jsx("label", { className: "block text-sm font-medium text-gray-700 mb-1", children: "End Date" }), _jsx("input", { type: "date", onChange: (e) => setFilters((prev) => ({
                                            ...prev,
                                            endDate: e.target.value || undefined,
                                            page: 1,
                                        })), className: "input" })] })] })] }), _jsx(DataTable, { columns: columns, data: logs, isLoading: isLoading, emptyMessage: "No audit logs found" })] }));
};
